import { Button, Container, Link, styled, Typography } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import config from '../../config'
import { activeStepState, formDataState } from '../helpers'
import Layout from '../layout/layout.component'
import PropTypes from 'prop-types'
import AppStoreButton from '../layout/appstore.component'

const PREFIX = 'SuccessError'

export const classes = {
  containerTop: `${PREFIX}-container-top`,
  containerMid: `${PREFIX}-container-mid`,
  containerBottom: `${PREFIX}-container-bottom`,
  label: `${PREFIX}-label`,
  marginTop: `${PREFIX}-margin-top`,
  registerAnother: `${PREFIX}-register-another`,
  appstoreContainer: `${PREFIX}-appstore-container`,
}

export const styles = ({ theme }) => ({
  [`& .${classes.containerTop}`]: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  [`& .${classes.containerMid}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(5, 0, 3, 0),
    padding: theme.spacing(0, 4),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 20),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 30),
    },
  },
  [`& .${classes.containerBottom}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 30),
    },
  },
  [`& .${classes.containerBottom} > h6 > span`]: {
    fontWeight: '700',
    fontSize: theme.spacing(2.3),
  },
  [`& .${classes.label}`]: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  [`& .${classes.marginTop}`]: {
    marginTop: '30px',
  },
  [`& .${classes.registerAnother}`]: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  [`& .${classes.appstoreContainer}`]: {
    [theme.breakpoints.up('md')]: {
      width: '65%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '50%',
    },
  },
})

const StyledDiv = styled('div')(styles)

ActivationResult.propTypes = {
  canRetry: PropTypes.bool.isRequired,
  handleRetry: PropTypes.func.isRequired,
}

function ActivationResult({ canRetry, handleRetry }) {
  const formData = useRecoilValue(formDataState)
  const navigate = useNavigate()
  const setActiveStep = useSetRecoilState(activeStepState)

  const getErrorContent = (errorType) => {
    switch (errorType) {
      case 1:
        return (
          <>
            Need help? Chat with us via{' '}
            <Link href="https://www.m1.com.sg" target="_blank" rel="noopener">
              m1.com.sg
            </Link>{' '}
            from 9:00 AM to 6:00 PM. Please prepare your mobile number and the
            18-digit SIM card serial number found on the back of your SIM card.
          </>
        )
      case 2:
        return 'SIM Activation is in progress. Please wait a while and try again later.'
      case 3:
        return 'Customer exceeds the maximum of 3 global prepaid lines. Please de-register one of the prepaid lines and try again.'
      default:
        return "Something went wrong. Let's try one more time!"
    }
  }

  const getErrorTitle = (errorTitleType) => {
    switch (errorTitleType) {
      case 1:
        return 'Port-in Error'
      default:
        return 'Oops'
    }
  }

  const getErrorIcon = (errorType) => {
    switch (errorType) {
      case 1:
        return <img src={config.PUBLIC_URL + '/error_2.svg'} alt="icon" />
      case 2:
        return <img src={config.PUBLIC_URL + '/error.svg'} alt="icon" />
      default:
        return <img src={config.PUBLIC_URL + '/error.svg'} alt="icon" />
    }
  }

  const handleClick = (canRetry) => {
    if (!canRetry) {
      navigate('/')
    } else {
      handleRetry(canRetry)
    }
  }

  const getButtonText = (canRetry) => {
    if (!canRetry) {
      return 'Home'
    }
    return 'Try again'
  }

  const getSuccessContent = () => {
    return (
      <>
        Your new SIM card is now activated.
        <br />
        <br />
        This will be your temporary number
        <br />
        <Typography sx={{ marginTop: '10px' }} variant="h2">
          {formData.msisdn}
        </Typography>
        <br />
      </>
    )
  }

  const getSuccessIcon = () => {
    return <img src={config.PUBLIC_URL + '/success_2.svg'} alt="icon" />
  }

  const handleClickAfterActivate = () => {
    setActiveStep(6)
  }

  return (
    <Layout
      showMobileMenu
      content={
        <StyledDiv>
          <Container className={classes.containerMid}>
            {formData.isOnboardSuccess
              ? getSuccessIcon()
              : getErrorIcon(!canRetry ? 1 : formData.onboardErrorType)}
            <Typography variant="h2" color="primary" sx={{ m: '10px 0px' }}>
              {formData.isOnboardSuccess
                ? 'SIM activation is successful!'
                : getErrorTitle(!canRetry ? 1 : formData.onboardErrorTitleType)}
            </Typography>
            <Typography variant="subtitle2" textAlign="center">
              {formData.isOnboardSuccess
                ? getSuccessContent()
                : getErrorContent(!canRetry ? 1 : formData.onboardErrorType)}
            </Typography>
            {formData.isOnboardSuccess ? (
              <Button
                variant="contained"
                color="primary"
                disabled={false}
                onClick={() => handleClickAfterActivate()}
                sx={{ mr: false, width: { xs: '90%', md: '50%' } }}
              >
                Port-in existing number
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={false}
                onClick={() => handleClick(canRetry)}
                sx={{ mr: false, width: { xs: '90%', md: '50%' } }}
              >
                {getButtonText(canRetry, formData.errorType)}
              </Button>
            )}
          </Container>
          <Container className={classes.containerBottom}>
            {(!canRetry || formData.isOnboardSuccess) && (
              <>
                <Typography
                  variant="subtitle2"
                  align="center"
                  fontWeight="600"
                  className={classes.marginTop}
                >
                  Download M1 Prepaid App to manage your account <br />
                  <br />
                </Typography>
                <AppStoreButton />
              </>
            )}
          </Container>
        </StyledDiv>
      }
    />
  )
}

export default ActivationResult
